import React, { useState } from 'react';
import { Clock, CheckCircle, XCircle } from 'lucide-react';
import { ContentSection } from '../layout/Layout';
import { Loading } from '../common/Loading';
import { ErrorMessage } from '../common/ErrorMessage';
import { DashboardAppointmentCard } from './DashboardAppointmendCard'
import { useTranslations } from '../../hooks/useTranslations';
import { AppointmentDayScheduleModal } from '../modals/AppointmentDayScheduleModal';

export function PendingApprovalsSection({
  pendingAppointments,
  loading,
  error,
  onAppointmentUpdate,
  onAppointmentClick,
  invalidateCacheForDate
}) {
  const [processingAppointments, setProcessingAppointments] = useState(new Set());
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [selectedScheduleDate, setSelectedScheduleDate] = useState('');
  const [highlightAppointmentId, setHighlightAppointmentId] = useState(null);

  const t = useTranslations()

  const handleAppointmentAction = async (appointment, newStatus) => {
    const appointmentId = appointment.id;

    // Add to processing set
    setProcessingAppointments(prev => new Set([...prev, appointmentId]));

    try {
      await onAppointmentUpdate({ ...appointment, status: newStatus }, 'edit');
      if (appointment.date || appointment.date) {
        const appointmentDate = appointment.date || appointment.date;
        // Call the cache invalidation function from the hook
        invalidateCacheForDate(appointmentDate);
      }
    } catch (error) {
      console.error('Error updating appointment:', error);
    } finally {
      // Remove from processing set
      setProcessingAppointments(prev => {
        const newSet = new Set(prev);
        newSet.delete(appointmentId);
        return newSet;
      });
    }
  };

  const sectionTitle = (
    <div className="flex items-center space-x-2">
      <Clock className="w-5 h-5 ml-2 text-amber-600" />
      <span className="text-lg font-bold text-gray-900">ממתין לאישור</span>
      {pendingAppointments && pendingAppointments.length > 0 && (
        <span className="bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full font-medium">
          {pendingAppointments.length}
        </span>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="lg:col-span-2">
        <ContentSection title={sectionTitle}>
          <Loading text="טוען תורים ממתינים..." />
        </ContentSection>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lg:col-span-2">
        <ContentSection title={sectionTitle}>
          <ErrorMessage
            title="שגיאה בטעינת תורים ממתינים"
            message={`לא ניתן לטעון תורים ממתינים לאישור: ${error}`}
            compact
          />
        </ContentSection>
      </div>
    );
  }

  const getAppointmentDetails = (appointment) => ({
    customerName: appointment.customer_name || t?.unknownCustomer || 'לקוח',
    serviceName: appointment.service_name || t?.unknownService || 'שירות',
    customerPhone: appointment.customer_phone || t?.noPhone || 'ללא טלפון',
    duration: appointment.duration || 45
  });

  // Helper functions
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="lg:col-span-3">
      <ContentSection title={sectionTitle}>
        <div className="space-y-4">
          {pendingAppointments && pendingAppointments.length > 0 ? (
            pendingAppointments.map((appointment) => {
              const isProcessing = processingAppointments.has(appointment.id);

              return (
                <div key={appointment.id} className="space-y-3">
                  {/* Loading Progress Bar - only show when processing */}
                  {isProcessing && (
                    <div className="h-1 bg-amber-200 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500 animate-pulse"></div>
                    </div>
                  )}

                  {/* Use AppointmentCard with pending variant */}
                  <div className={isProcessing ? 'opacity-75' : ''}>
                    <DashboardAppointmentCard
                      appointment={appointment}
                      onAppointmentClick={onAppointmentClick}
                      getAppointmentDetails={getAppointmentDetails}
                      getInitials={getInitials}
                      variant="pending"
                      t={t}
                    />
                  </div>

                  {/* Action Buttons - below the card */}
                  <div className="flex items-center gap-3 px-4">
                    <button
                      disabled={isProcessing}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAppointmentAction(appointment, 'scheduled');
                      }}
                      className="flex-1 max-w-[120px] flex items-center justify-center gap-2 px-4 py-2.5 
                        bg-gradient-to-r from-emerald-500 to-green-500 
                        hover:from-emerald-600 hover:to-green-600
                        disabled:from-gray-300 disabled:to-gray-400
                        text-white text-sm font-medium rounded-xl
                        backdrop-blur-sm shadow-lg shadow-emerald-500/25
                        transition-all duration-200 ease-out
                        hover:shadow-emerald-500/40 hover:scale-[1.02]
                        active:scale-[0.98] disabled:hover:scale-100"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>אשר</span>
                    </button>

                    <button
                      disabled={isProcessing}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAppointmentAction(appointment, 'cancelled');
                      }}
                      className="flex-1 max-w-[120px] flex items-center justify-center gap-2 px-4 py-2.5
                        bg-white/80 hover:bg-white/90
                        border border-red-200/60 hover:border-red-300/80
                        text-red-600 hover:text-red-700 text-sm font-medium rounded-xl
                        backdrop-blur-sm shadow-lg shadow-red-500/10
                        transition-all duration-200 ease-out
                        hover:shadow-red-500/20 hover:scale-[1.02]
                        active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100"
                    >
                      <XCircle className="w-4 h-4" />
                      <span>דחה</span>
                    </button>

                    <button
                      disabled={isProcessing}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedScheduleDate(appointment.date || appointment.date);
                        setHighlightAppointmentId(appointment.id);
                        setScheduleModalOpen(true);
                      }}
                      className="flex-1 max-w-[120px] flex items-center justify-center gap-2 px-4 py-2.5
                        bg-white/80 hover:bg-white/90
                        border border-blue-200/60 hover:border-blue-300/80
                        text-blue-600 hover:text-blue-700 text-sm font-medium rounded-xl
                        backdrop-blur-sm shadow-lg shadow-blue-500/10
                        transition-all duration-200 ease-out
                        hover:shadow-blue-500/20 hover:scale-[1.02]
                        active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100"
                    >
                      <Clock className="w-4 h-4" />
                      <span>יומן</span>
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-12 bg-gradient-to-r from-white to-amber-50 rounded-lg border border-amber-200">
              <div className="flex justify-center mb-4">
                <CheckCircle className="w-16 h-16 text-emerald-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">אין תורים ממתינים</h3>
              <p className="text-gray-600">כל התורים אושרו או שלא קיימים תורים חדשים לאישור.</p>
            </div>
          )}
        </div>
      </ContentSection>

      <AppointmentDayScheduleModal
        isOpen={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        targetDate={selectedScheduleDate}
        highlightAppointmentId={highlightAppointmentId}
      />
    </div>
  );
}